import { Modal, Tabs, message } from "antd"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import styled from "styled-components"

import BottomSheet from "../../../../../_shared/components/BottomSheet"
import { useStateValue } from "../../../../../_shared/context/AppStateStore"
import useRestTable from "../../../../../_shared/hooks/usePaginatedRestResource"
import BioForm from "./BioForm"
import DateChangeForm from "./DateChangeForm"
import DateForm from "./DateForm"
import ImageForm from "./ImageForm"
import LocationForm from "./LocationForm"
import SlotForm from "./SlotForm"

const { TabPane } = Tabs

// ----------------------------------------------------------------------------

function EventsBottomSheet(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { open, onConfirm, onCancel, event } = props

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [{ token }] = useStateValue()

    const [eventTypes] = useRestTable("/event-types", token)

    // This component refuses to work with useForm or form object. Drastic measures
    // have been taken
    const [title, setTitle] = useState(event?.title)
    const [eventType, setEventType] = useState(event?.eventType)
    const [description, setDescription] = useState(event?.description)
    const [summary, setSummary] = useState(event?.summary)
    const [minimumAge, setMinimumAge] = useState(event?.minimumAge ?? 35)
    const [maximumAge, setMaximumAge] = useState(event?.maximumAge ?? 49)
    const [slots, setSlots] = useState(event?.slots)
    const [days, setDays] = useState(event?.date)
    const [url, setUrl] = useState(event?.url)
    const [detailUrl, setDetailUrl] = useState(event?.url)
    const [price, setPrice] = useState(event?.price)
    const [bookingsOpen, setBookingsOpen] = useState(event?.bookingsOpen)
    const [published, setPublished] = useState(event?.published)
    const [isPrivate, setPrivate] = useState(event?.private)
    const [autoPublishDate, setAutoPublishDate] = useState(
        event?.autoPublishDate || null
    )

    const [locations, setLocations] = useState([])

    // Date change data is handled separately to prevent slot regeneration
    const [dateChangeData, setDateChangeData] = useState({
        shouldUpdateDate: false,
        eventStartDate: null,
    })

    // -------------------------------------
    // Effects
    // -------------------------------------

    function setFormValues(values) {
        Object.entries(values).forEach(([key, item]) => {
            if (key === "slots") {
                setSlots(item.map((s) => ({ ...s })))
            } else if (["price", "minimumAge", "maximumAge"].includes(key)) {
                handleValueChange(key)({ target: { value: item } })
            } else {
                handleValueChange(key)(item)
            }
        })
    }

    useEffect(() => {
        setFormValues({
            title: event?.title,
            summary: event?.summary,
            slots: event?.slots ? [...event?.slots] : [],
            location: { ...event?.location },
            description: event?.description,
            url: event?.url,
            detailUrl: event?.detailUrl,
            duration: event?.duration,
            days: event?.days,
            price: event?.price,
            published: event?.published,
            bookingsOpen: event?.bookingsOpen,
            autoPublishDate: event?.autoPublishDate,
            minimumAge: event?.minimumAge ?? 35,
            maximumAge: event?.maximumAge ?? 49,
            eventType: event?.eventType,
            private: event?.private,
            locations: event?.locations,
        })

        //eslint-disable-next-line
    }, [event])

    useEffect(() => {
        !open &&
            setDateChangeData({ shouldUpdateDate: false, eventStartDate: null })
    }, [open])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function handleConfirm() {
        function execute() {
            const error = checkEventValid()
            if (error) {
                message.error(error)
                return
            }
            onConfirm &&
                onConfirm({
                    id: event?.id,
                    title,
                    summary,
                    description,
                    minimumAge,
                    maximumAge,
                    days,
                    url,
                    detailUrl,
                    slots,
                    locations,
                    published,
                    bookingsOpen,
                    price: price || 0,
                    shouldUpdateDate: dateChangeData?.shouldUpdateDate,
                    newStartDate: dateChangeData?.eventStartDate,
                    autoPublishDate,
                    eventType: eventType?.id ? eventType.id : eventType,
                    private: isPrivate,
                })
        }

        if (dateChangeData?.shouldUpdateDate) {
            Modal.confirm({
                onOk: execute,
                title: "Conferma modifica evento",
                content: confirmMessage,
            })
        } else {
            execute()
        }
    }

    function handleSubmit(e) {
        e.preventDefault()
    }

    function handleValueChange(field) {
        const MAP = {
            title: (e) => setTitle(e),
            summary: (e) => setSummary(e),
            description: (e) => setDescription(e),
            days: (e) => setDays(e),
            slots: (e) => setSlots(e),
            url: (e) => setUrl(e),
            price: (e) => setPrice(e.target.value),
            published: (e) => setPublished(e),
            bookingsOpen: (e) => setBookingsOpen(e),
            detailUrl: (e) => setDetailUrl(e),
            autoPublishDate: (e) => setAutoPublishDate(e),
            minimumAge: (e) => setMinimumAge(e.target.value),
            maximumAge: (e) => setMaximumAge(e.target.value),
            eventType: (e) => setEventType(e),
            private: (e) => setPrivate(e),
            locations: (e) => setLocations(e),
        }
        return (value) => {
            try {
                MAP[field](value)
            } catch (e) {
                //
            }
        }
    }

    function handleImageUpload(key, image) {
        key === "url" ? image && setUrl(image) : image && setDetailUrl(image)
    }

    function handleClose() {
        onCancel && onCancel()
    }

    function checkEventValid() {
        if (!days || days?.length === 0) {
            return "Si deve inserire almeno una giornata per l'evento."
        }
        if (!url) {
            return "Nessuna immagine caricata per l'evento"
        }
        if (!title || !summary || !description) {
            return "Inserire titolo, descrizione e riassunto."
        }
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------
    const tempFormValues = {
        title,
        summary,
        description,
        days,
        url,
        detailUrl,
        slots,
        price,
        published,
        bookingsOpen,
        autoPublishDate,
        minimumAge,
        eventType,
        maximumAge,
        private: isPrivate,
        locations,
        id: event?.id,
    }

    return (
        <BottomSheet
            open={open}
            className={props.className}
            onCancel={handleClose}
            onConfirm={handleConfirm}
            title={event?.id ? "Modifica evento" : "Crea evento"}
            snapPoint={90}
        >
            <form className="form" onSubmit={handleSubmit}>
                <Tabs tabPosition="top">
                    <TabPane tab="Dettagli" key="details">
                        <BioForm
                            values={tempFormValues}
                            onValueChange={handleValueChange}
                            eventTypes={eventTypes}
                        />
                    </TabPane>
                    {!event?.id && (
                        <TabPane tab="Data" key="date">
                            <DateForm
                                values={tempFormValues}
                                onValueChange={handleValueChange}
                                eventTypes={eventTypes}
                            />
                        </TabPane>
                    )}
                    {event?.id && (
                        <TabPane
                            tab={event?.id ? "Modifica data" : "Data"}
                            key="change-date"
                        >
                            <DateChangeForm
                                values={dateChangeData}
                                onValueChange={setDateChangeData}
                                eventData={tempFormValues}
                                isNewEvent={event?.id ? false : true}
                                eventTypes={eventTypes}
                            />
                        </TabPane>
                    )}
                    <TabPane tab="Immagine" key="gallery">
                        <ImageForm
                            values={tempFormValues}
                            onUpload={handleImageUpload}
                            eventTypes={eventTypes}
                        />
                    </TabPane>
                    <TabPane tab="Location" key="location">
                        <LocationForm
                            values={tempFormValues}
                            onValueChange={handleValueChange}
                            eventTypes={eventTypes}
                            initialValues={event}
                        />
                    </TabPane>
                    <TabPane tab="Slot orari" key="slots">
                        <SlotForm
                            values={tempFormValues}
                            onValueChange={handleValueChange}
                            eventTypes={eventTypes}
                        ></SlotForm>
                    </TabPane>
                </Tabs>
            </form>
        </BottomSheet>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

EventsBottomSheet.propTypes = {
    className: PropTypes.string.isRequired,
    open: PropTypes.bool,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    event: PropTypes.object,
    reloadEvent: PropTypes.func,
}

EventsBottomSheet.defaultProps = {}

// ----------------------------------------------------------------------------

const StyledEventsBottomSheet = styled(EventsBottomSheet)`
    & {
    }
`
// ----------------------------------------------------------------------------

export default StyledEventsBottomSheet

const confirmMessage =
    "Le date dell'evento hanno subito modifiche. Le prenotazioni verrano spostate sulle giornate corrispondenti e gli utenti prenotati riceveranno un'email di notifica del cambio di data."
